<template>
  <div v-if="items.length" class="rec">
    <button type="button" class="rec__toggle" @click="open = !open">
      <span class="rec__icon"><hero-icon :icon="SparklesIcon" :size="18" /></span>
      <span class="rec__text">
        <span class="rec__name">Propiedades recomendadas</span>
        <span class="rec__sub">
          {{ items.length }} {{ items.length === 1 ? "coincidencia" : "coincidencias" }}
        </span>
      </span>
      <hero-icon
        :icon="ChevronDownIcon"
        :size="16"
        class="rec__chev"
        :class="{ 'rec__chev--open': open }"
      />
    </button>

    <div v-if="open" class="rec__row">
      <button
        v-for="p in items"
        :key="p.id"
        type="button"
        class="rec-card"
        @click="openProperty(p)"
      >
        <span class="rec-card__media" :class="{ 'rec-card__media--empty': !cover(p) }">
          <img v-if="cover(p)" :src="cover(p)" alt="" />
          <hero-icon v-else :icon="HomeIcon" :size="22" />
        </span>
        <span class="rec-card__body">
          <span class="rec-card__addr">{{ p.fullAddress }}</span>
          <span class="rec-card__meta">{{ meta(p) }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import HeroIcon from "@/components/shared/HeroIcon";
import HomeIcon from "heroicons/24/outline/home.svg";
import ChevronDownIcon from "heroicons/24/outline/chevron-down.svg";
import SparklesIcon from "heroicons/24/outline/sparkles.svg";
import { PropertyRecommendations } from "@/graphql/queries/inquiries";

const OP_LABELS = { sell: "Venta", rent: "Alquiler" };

export default {
  name: "Recommendations",

  components: { HeroIcon },

  props: {
    inquiryId: {
      type: [String, Number],
      required: true
    },
    limit: {
      type: Number,
      default: 8
    }
  },

  data: () => ({
    HomeIcon,
    ChevronDownIcon,
    SparklesIcon,
    open: false,
    items: [],
    loading: true
  }),

  created() {
    this.fetch();
  },

  methods: {
    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: PropertyRecommendations,
          variables: { inquiryId: this.inquiryId, limit: this.limit },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.items = res.data.propertyRecommendations || [];
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    cover(p) {
      const imgs = p.images || [];
      if (!imgs.length) return null;
      const path = imgs[0].smallUrl || imgs[0].thumbUrl;
      return path ? process.env.VUE_APP_RAILS_URL + path : null;
    },

    meta(p) {
      const bits = [];
      if (p.propertyType) bits.push(this.$t("property.type." + p.propertyType.toLowerCase()));
      const price = this.price(p);
      if (price) bits.push(price);
      return bits.join(" · ");
    },

    price(p) {
      const ops = p.operationTypes || [];
      if ((ops.includes("sell") || !ops.length) && p.sellPrice)
        return this.money(p.sellCurrency, p.sellPrice);
      if ((ops.includes("rent") || !ops.length) && p.price)
        return this.money(p.currency, p.price);
      return "";
    },

    money(currency, value) {
      const n = Number(value);
      const num = isNaN(n) ? value : n.toLocaleString("es-AR");
      return `${currency || ""} ${num}`.trim();
    },

    cap(s) {
      return s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s;
    },

    openProperty(p) {
      const path = this.$router.resolve({
        name: "Property",
        params: { id: p.id }
      });
      window.open(path.href, "_blank");
    }
  }
};
</script>

<style scoped>
.rec {
  background: #faf8f4;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 10px 12px;
}
.rec__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.rec__icon {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 9px;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rec__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}
.rec__name { font-size: 13.5px; font-weight: 600; color: #1a1814; }
.rec__sub { font-size: 12px; color: #756f64; }
.rec__chev {
  flex: none;
  color: #a39a8c;
  transition: transform 0.18s ease;
}
.rec__chev--open { transform: rotate(180deg); }

.rec__row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  margin-top: 12px;
  padding-bottom: 4px;
  scrollbar-width: thin;
}
.rec-card {
  flex: 0 0 180px;
  width: 180px;
  text-align: left;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rec-card:hover {
  border-color: #dcd4c6;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}
.rec-card__media {
  display: block;
  height: 104px;
  background: #ece7de;
}
.rec-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.rec-card__media--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c4bcae;
}
.rec-card__body { display: block; padding: 9px 11px 11px; }
.rec-card__addr {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #1a1814;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.rec-card__meta {
  display: block;
  margin-top: 4px;
  font-size: 11.5px;
  color: #756f64;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
